import { useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { COUNTRIES, type Country } from '@/constants/countries';
import { cn } from '@/lib/utils';

interface InternationalPhoneFieldProps {
  id: string;
  label: string;
  countryLabel: string;
  searchLabel: string;
  notFoundLabel: string;
  placeholder: string;
  country: string;
  phone: string;
  onCountryChange: (country: string) => void;
  onPhoneChange: (phone: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  describedBy?: string;
  required?: boolean;
  privateReplayBlock?: boolean;
}

function foldDiacritics(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

export function InternationalPhoneField({
  id,
  label,
  countryLabel,
  searchLabel,
  notFoundLabel,
  placeholder,
  country,
  phone,
  onCountryChange,
  onPhoneChange,
  disabled = false,
  invalid = false,
  describedBy,
  required = false,
  privateReplayBlock = false,
}: InternationalPhoneFieldProps) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const selectedCountry = COUNTRIES.find((item) => item.name === country) ?? COUNTRIES[0];
  const displayNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([i18n.language], { type: 'region' });
    } catch {
      return null;
    }
  }, [i18n.language]);
  const countryName = (item: Country) => displayNames?.of(item.code) ?? item.name;
  const selectedCountryName = countryName(selectedCountry);

  const selectCountry = (nextCountry: Country) => {
    onCountryChange(nextCountry.name);
    onPhoneChange('');
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="grid gap-2 sm:grid-cols-[13rem_minmax(0,1fr)]">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              ref={triggerRef}
              type="button"
              variant="outline"
              role="combobox"
              aria-label={`${countryLabel}: ${selectedCountryName} (${selectedCountry.phoneCode})`}
              aria-expanded={open}
              disabled={disabled}
              className="h-11 w-full justify-between bg-background px-3 text-foreground hover:bg-accent"
            >
              <span className="min-w-0 truncate sm:hidden">
                {selectedCountry.flag} {selectedCountryName} ({selectedCountry.phoneCode})
              </span>
              <span className="hidden min-w-0 truncate sm:block">
                {selectedCountry.flag} {selectedCountryName} ({selectedCountry.phoneCode})
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            data-sentry-block={privateReplayBlock ? '' : undefined}
            data-viralize-qualification-private={
              privateReplayBlock ? '' : undefined
            }
            className={cn(
              'w-[var(--radix-popover-trigger-width)] p-0 sm:w-80',
              privateReplayBlock && 'rr-block',
            )}
          >
            <Command label={searchLabel}>
              <CommandInput placeholder={searchLabel} />
              <CommandList className="max-h-60">
                <CommandEmpty>{notFoundLabel}</CommandEmpty>
                <CommandGroup>
                  {COUNTRIES.map((item) => (
                    <CommandItem
                      key={item.code}
                      className="min-h-11"
                      value={`${countryName(item)} ${item.name} ${item.code} ${item.phoneCode}`}
                      keywords={[
                        countryName(item),
                        item.name,
                        item.code,
                        item.phoneCode,
                        foldDiacritics(countryName(item)),
                        foldDiacritics(item.name),
                      ]}
                      onSelect={() => selectCountry(item)}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          item.name === selectedCountry.name ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <span className="mr-2" aria-hidden="true">{item.flag}</span>
                      <span className="min-w-0 flex-1 truncate">{countryName(item)}</span>
                      <span className="ml-3 text-muted-foreground">{item.phoneCode}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <Input
          id={id}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          value={phone}
          onChange={(event) => onPhoneChange(event.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          aria-required={required || undefined}
          required={required}
          className="h-11 w-full bg-background"
        />
      </div>
    </div>
  );
}
